// hooks/useJWPlayer.js para usar en videos de JWP que cargan de forma dinamica, por ejemplo en el home SliderJWP
import { useEffect, useRef } from "react";
import gamVideoTags from "@/shared/Connatix/gam_video_tags.json";
import { getUserID } from "helpers";

/**
 * getAdTags - Busca las URLs de ad tags (pre/mid) para un tipo de player JWP.
 * Si no encuentra tags para el tipo, usa el fallback "player_all_other_visits".
 *
 * @param {string} type - Tipo de player (ej: "sliderhome", "vertical", etc.)
 * @returns {{ ad_tag_pre: string, ad_tag_mid: string }}
 *
 * Ejemplo:
 *   const { ad_tag_pre, ad_tag_mid } = getAdTags("sliderhome");
 */
export const getAdTags = (type, typeparent = "", isUsa = false) => {
    const matchingTags = typeparent
        ? gamVideoTags.filter((tag) => tag.typeParent === typeparent && tag.type === type)
        : gamVideoTags.filter((tag) => tag.type === type);
      let ad_tag_pre = matchingTags.find((tag) => tag.offset === "pre")?.url || "";
      let ad_tag_mid = matchingTags.find((tag) => tag.offset === "mid")?.url || "";
      if(isUsa && ad_tag_pre == ""){
        ad_tag_pre= gamVideoTags.find(
          (tag) => tag.nombre === "player_all_other_visits_usa" && tag.offset === "pre"
        )?.url || "";
      }else if(ad_tag_pre == ""){
        ad_tag_pre= gamVideoTags.find(
          (tag) => tag.nombre === "player_all_other_visits" && tag.offset === "pre"
        )?.url || "";
        ad_tag_mid= gamVideoTags.find(
          (tag) => tag.nombre === "player_all_other_visits" && tag.offset === "mid"
        )?.url || "";
      }

    return { ad_tag_pre, ad_tag_mid };
};

/**
 * Si no se pasan ad_tag_pre/ad_tag_mid, se resuelven automáticamente
 * usando getAdTags con el type proporcionado.
 *
 * @param {Object} params
 * @param {string} params.playlistId - ID de la playlist de JWP
 * @param {string} params.placementId - ID del placement de JWP
 * @param {string} [params.type="sliderhome"] - Tipo de player (usado para buscar ad tags)
 * @param {string} [params.ad_tag_pre] - URL del ad tag pre-roll (auto-resuelto si no se pasa)
 * @param {string} [params.ad_tag_mid] - URL del ad tag mid-roll (auto-resuelto si no se pasa)
 * @returns {React.RefObject} - Ref para asignar al contenedor React (ref={containerRef})
 */
// placementIds / media_ids que ya tienen un player inyectado en la página. Evita que el mismo
// placement se embeba dos veces (dos instancias con el mismo placementId/media_id, o un
// re-mount)
const embeddedPlacements = new Set();
const embeddedMediaIds = new Set();

const useJWPlayer = ({ playlistId, placementId, type = "sliderhome", ad_tag_pre, ad_tag_mid, media_id = "", typeparent = "", recommendations_id = "" }) => {
    const containerRef = useRef(null);

    useEffect(() => {

      if (!containerRef.current) return;

      // Si este placement o media_id ya tiene un player en la página, no volver a inyectar el embed.
      if (embeddedPlacements.has(placementId) || document.getElementById(`jwPlacementDiv_${placementId}`)) {
        console.warn(`[useJWPlayer] Se intentó duplicar el player del placement "${placementId}". Embed bloqueado.`);
        return;
      }

      // Si ya existe un script con el mismo media_id, no volver a inyectar.
      if (media_id && (embeddedMediaIds.has(media_id) || document.getElementById(`jwPlacementDiv_${media_id}`))) {
        console.warn(`[useJWPlayer] Se intentó duplicar el script con media_id "${media_id}". Embed bloqueado.`);
        return;
      }
      
      // Auto-resolver ad tags si no se proporcionaron
      let resolvedAdTagPre = ad_tag_pre !== undefined ? ad_tag_pre : getAdTags(type, typeparent).ad_tag_pre;
      let resolvedAdTagMid = ad_tag_mid !== undefined ? ad_tag_mid : getAdTags(type, typeparent).ad_tag_mid;

      const userId = getUserID() || "";
      resolvedAdTagPre = resolvedAdTagPre.replace("[placeholder]", userId);
      resolvedAdTagMid = resolvedAdTagMid.replace("[placeholder]", userId);


      containerRef.current.innerHTML = "";
      embeddedPlacements.add(placementId);
      if (media_id) embeddedMediaIds.add(media_id);


      const jwDataStore = window.jwDataStore || { custom: {} };
      jwDataStore.custom[placementId] = {
          type,
          ...(playlistId  && { playlist_id: playlistId }),
          ...(resolvedAdTagPre && { ad_tag_pre: resolvedAdTagPre }),
          ...(resolvedAdTagMid && { ad_tag_mid: resolvedAdTagMid }),
          ...(media_id  && { media_id: media_id }),
          ...(typeparent  && { typeparent: typeparent }),
          ...(recommendations_id  && { recommendations_id: recommendations_id }),

      };
      window.jwDataStore = jwDataStore;

      const script = document.createElement("script");
      script.src = `https://cdn.jwplayer.com/v2/sites/${process.env.NEXT_PUBLIC_CONNATIX_SITE_ID}/placements/${placementId}/embed.js`;
      script.async = true;
      script.id = `jwMediaId_${media_id || playlistId}`;

      containerRef.current.appendChild(script);

      return () => {
          embeddedPlacements.delete(placementId);
          if (media_id) embeddedMediaIds.delete(media_id);
          if (containerRef.current && containerRef.current.parentNode) {
            containerRef.current.parentNode.removeChild(containerRef.current);
          }
      };
    }, [playlistId, placementId, type]);

    return containerRef;
};

export default useJWPlayer;